// src/features/system/auth/Login.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { bff } from '../../../lib/http/bff';
import { useAuth } from '@/shared/auth';
import { useQueryClient } from '@tanstack/react-query';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const { refresh } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    try {
      localStorage.removeItem('jwt');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('app_jwt');
    } catch {}
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setSubmitting(true);
    try {
      await bff.post<void, { username: string; password: string }>(
        '/auth/login',
        { username, password }
      );

      // Immediately pull fresh session (user/roles/perms/nav)
      await refresh();

      // Keep any guards using react-query in sync
      await queryClient.invalidateQueries({ queryKey: ['me'] });

      navigate('/', { replace: true });
    } catch (err: any) {
      const msg = err?.body?.detail || err?.body?.error || err?.message || 'Login failed.';
      setErrorMsg(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="w-full max-w-sm p-6 bg-neutral-900 text-white rounded-lg shadow-lg">
        <h1 className="text-2xl mb-4 text-center">Login</h1>
        {errorMsg && <p className="text-red-400 mb-2 text-center">{errorMsg}</p>}

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div>
            <label htmlFor="user" className="block text-sm mb-1">Username</label>
            <input
              id="user"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
              placeholder="Enter your username"
              autoComplete="username"
              spellCheck={false}
              required
              disabled={submitting}
            />
          </div>

          <div>
            <label htmlFor="pass" className="block text-sm mb-1">Password</label>
            <input
              id="pass"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
              placeholder="Enter your password"
              autoComplete="current-password"
              required
              disabled={submitting}
            />
          </div>

          <button
            type="submit"
            disabled={submitting || !username || !password}
            className="w-full bg-emerald-800 hover:bg-emerald-700 px-4 py-2 rounded-lg text-white font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            aria-busy={submitting}
          >
            {submitting ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}
